// =============================================================================
// Adapter: p5 Compositor Hooks
// =============================================================================

import {
  setCompositorRuntime,
  blitDefaultFramebufferSource,
} from "../../core/compositor_runtime.js";

function clearTarget(renderer, target, isFramebufferTarget) {
  if (!target) return;

  if (isFramebufferTarget(target)) {
    target.draw(() => renderer.clear());
    return;
  }

  const ctx = target.drawingContext;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, target.width, target.height);
  ctx.restore();
}

function ensureBlendShaderProgram(renderer, vertSrc, fragSrc, brushFragSrc, watercolorFragSrc) {
  if (!renderer.loaded) {
    renderer.shaderProgram ??= renderer.createShader(vertSrc, fragSrc);
    renderer.brushShaderProgram ??= renderer.createShader(vertSrc, brushFragSrc);
    renderer.watercolorShaderProgram ??= renderer.createShader(vertSrc, watercolorFragSrc);
    renderer.loaded = true;
  }
}

function ensureBlendSourceFramebuffer(
  renderer,
  currentFramebuffer,
  width,
  height,
  density,
) {
  if (currentFramebuffer?.remove) currentFramebuffer.remove();
  const fb = renderer.createFramebuffer({
    width,
    height,
    density,
    antialias: false,
    depth: false,
    stencil: false,
  });
  return fb;
}

const createFramebuffer = (renderer, options) =>
  renderer.createFramebuffer(options);

function runBlendShaderPass({
  renderer,
  shader,
  source,
  mask,
  color,
  pigment,
  uniforms,
  Cwidth,
  Cheight,
  dirtyRect,
  targetIsFramebuffer,
  withScissor,
}) {
  const gl = renderer.drawingContext;
  const hadDepthTest = gl.isEnabled(gl.DEPTH_TEST);

  renderer.push();
  try {
    gl.disable(gl.DEPTH_TEST);
    renderer.translate(0, 0);
    renderer.shader(shader);

    shader.setUniform("u_source", source);
    shader.setUniform("u_targetIsFramebuffer", targetIsFramebuffer);
    shader.setUniform("u_mask", mask);
    shader.setUniform("u_color", color);
    shader.setUniform("u_pigmentKS", pigment.ks);
    shader.setUniform("u_pigmentLuminance", pigment.luminance);
    if (uniforms) for (const name in uniforms) shader.setUniform(name, uniforms[name]);

    withScissor(
      gl,
      dirtyRect,
      () => {
        renderer.fill(0, 0, 0, 0);
        renderer.noStroke();
        renderer.rect(-Cwidth / 2, -Cheight / 2, Cwidth, Cheight);
      },
      !targetIsFramebuffer,
    );
  } finally {
    try {
      renderer.pop();
    } finally {
      renderer.resetShader();
      if (hadDepthTest) gl.enable(gl.DEPTH_TEST);
    }
  }
}

function blitSourceToFramebuffer(args) {
  if (args.isFramebufferTarget(args.sourceTarget)) {
    (args.sourceTarget.renderer ?? args.renderer)._renderer?.flushDraw?.();
    args.sourceFramebuffer.draw(() => {
      args.withScissor(
        args.renderer.drawingContext,
        args.dirtyRect,
        () => {
          args.renderer.clear();
          args.renderer.push();
          try {
            args.renderer.imageMode(args.renderer.CENTER);
            args.renderer.image(
              args.sourceTarget,
              0,
              0,
              args.Cwidth,
              args.Cheight,
            );
          } finally {
            args.renderer.pop();
          }
        },
        false,
      );
    });
    return args.sourceFramebuffer;
  }

  args.renderer._renderer?.flushDraw?.();
  return blitDefaultFramebufferSource(args);
}

export function initP5CompositorRuntime() {
  setCompositorRuntime({
    clearTarget,
    ensureBlendShaderProgram,
    ensureBlendSourceFramebuffer,
    createFramebuffer,
    runBlendShaderPass,
    blitSourceToFramebuffer,
  });
}
